import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import { BsFillMoonFill, BsFillSunFill, BsSun } from 'react-icons/bs';
import { FaSun } from 'react-icons/fa';
import { IoSunny } from 'react-icons/io5';
import { RiSunFill } from 'react-icons/ri';
import { useTheme } from '../../styles/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';
import logoImage from '../../assets/logo-gvw-tech-jardinage-2.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: transparent;
  transition: all var(--transition-medium);
  padding: 0;
  height: 80px;
  display: flex;
  align-items: center;
  
  &.scrolled {
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  .dark-mode & {
    background-color: transparent;
    
    &.scrolled {
      background-color: var(--dark);
    }
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-family: var(--font-secondary);
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--primary);
  transition: all var(--transition-fast);
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
  
  .dark-mode & {
    color: var(--white);
  }

  &:hover {
    transform: translateY(-2px);
    color: var(--primary);
    
    .dark-mode & {
      color: var(--white);
    }
  }
`;

const LogoImage = styled.img`
  height: 65px;
  width: auto;
  margin-right: 14px;
  transition: all var(--transition-fast);
  filter: brightness(0);
  
  @media (max-width: 768px) {
    height: 55px;
    margin-right: 10px;
  }
  
  @media (max-width: 480px) {
    height: 45px;
    margin-right: 8px;
  }
  
  .dark-mode & {
    filter: brightness(0) invert(1);
  }
  
  ${Logo}:hover & {
    transform: rotate(5deg);
  }
`;

const LogoHighlight = styled.span`
  color: var(--secondary);
  
  ${Logo}:hover & {
    color: var(--secondary);
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-100%')};
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background-color: var(--white);
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 5rem;
    padding-bottom: 2rem;
    transition: right var(--transition-medium);
    box-shadow: var(--shadow-lg);
    overflow-y: auto;
    z-index: 1000;

    .dark-mode & {
      background-color: var(--dark);
    }
  }
`;

const NavLink = styled.a`
  margin: 0 1.2rem;
  color: var(--gray-800);
  font-weight: 500;
  position: relative;
  transition: all var(--transition-fast);
  padding: 0.3rem 0;
  cursor: pointer;
  text-decoration: none;

  .dark-mode & {
    color: var(--gray-200);
  }

  &:hover {
    color: var(--secondary);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--secondary);
    transition: width var(--transition-fast);
  }

  &:hover::after {
    width: 100%;
  }
  
  &.active {
    color: var(--primary);
    
    .dark-mode & {
      color: var(--white);
    }
  }
  
  &.active::after {
    width: 100%;
    background-color: var(--secondary);
    
    .dark-mode & {
      background-color: var(--secondary);
    }
  }

  @media (max-width: 768px) {
    margin: 1.2rem 0;
    color: var(--dark);
    font-size: 1.2rem;

    .dark-mode & {
      color: var(--light);
    }
  }
`;

const ThemeToggle = styled.button`
  background-color: var(--gray-800);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--gray-400);
  margin-left: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  width: 42px;
  height: 42px;
  border-radius: var(--border-radius-circle);
  position: relative;
  padding: 0;
  line-height: 1;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  .dark-mode & {
    background-color: var(--gray-200);
    color: var(--gray-600);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    color: var(--secondary);
    
    svg {
      color: var(--secondary);
    }
    
    &::before {
      transform: scale(1.5);
      opacity: 0.1;
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, var(--secondary) 0%, transparent 70%);
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
    z-index: 0;
  }

  svg {
    font-size: 1.3rem;
    transition: color var(--transition-fast);
    position: relative;
    top: 1px;
    z-index: 1;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
  }
  
  .sun-icon {
    font-size: 1.5rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--gray-600);
  font-size: 1.8rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0.5rem;
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
    right: 1rem;
  }
  
  &:hover {
    color: var(--primary);
  }
  
  .dark-mode & {
    color: var(--gray-400);
    
    &:hover {
      color: var(--white);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--dark);
  font-size: 1.8rem;
  display: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius-circle);
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: rotate(90deg);
  }

  .dark-mode & {
    color: var(--light);
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  z-index: 998;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  height: 3px;
  background-color: var(--primary);
  bottom: -5px;
  border-radius: 3px;
  transition: all 0.3s ease;
  
  .dark-mode & {
    background-color: var(--white);
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { darkMode, toggleTheme } = useTheme();
  const [activeLink, setActiveLink] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Déterminer la section active en fonction du défilement
    const handleScroll = () => {
      // Gestion de la classe scrolled
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
      
      // Déterminer la section active
      const sections = ['accueil', 'services', 'realisations', 'a-propos', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveLink(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  };
  
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      closeMenu();
    }
  };

  // Choisissez l'icône que vous préférez parmi ces options
  const SunIcon = FaSun; // Vous pouvez changer pour BsFillSunFill, BsSun, FaSun, IoSunny ou RiSunFill

  return (
    <HeaderContainer className={isScrolled ? 'scrolled' : ''}>
      <NavContainer>
        <Logo to="/" as={Link}>
          <LogoImage src={logoImage} alt="Logo GVW-Tech" />
          GVW-<LogoHighlight>Tech</LogoHighlight>
        </Logo>

        <MenuButton onClick={toggleMenu} aria-label="Menu">
          <FiMenu />
        </MenuButton>

        <AnimatePresence>
          {isMenuOpen && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeMenu}
            />
          )}
        </AnimatePresence>

        <NavLinks isOpen={isMenuOpen}>
          <CloseButton onClick={closeMenu} aria-label="Fermer le menu">
            <FiX />
          </CloseButton>
          <NavLink 
            onClick={() => scrollToSection('accueil')}
            className={activeLink === 'accueil' ? 'active' : ''}
          >
            Accueil
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('services')}
            className={activeLink === 'services' ? 'active' : ''}
          >
            Services
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('realisations')}
            className={activeLink === 'realisations' ? 'active' : ''}
          >
            Réalisations
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('a-propos')}
            className={activeLink === 'a-propos' ? 'active' : ''}
          >
            À propos
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('contact')}
            className={activeLink === 'contact' ? 'active' : ''}
          >
            Contact
          </NavLink>
          <NavLink 
            onClick={() => scrollToSection('devis')}
            className={activeLink === 'devis' ? 'active' : ''}
            style={{ 
              backgroundColor: 'var(--secondary)', 
              color: 'var(--black)', 
              padding: '0.5rem 1rem', 
              borderRadius: 'var(--border-radius-sm)',
              fontWeight: '600'
            }}
          >
            Devis gratuit
          </NavLink>

          <ThemeToggle 
            onClick={toggleTheme}
            aria-label={darkMode ? "Passer en mode clair" : "Passer en mode sombre"}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={darkMode ? 'dark' : 'light'}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.3 }}
              >
                {darkMode ? <SunIcon className="sun-icon" /> : <BsFillMoonFill />}
              </motion.div>
            </AnimatePresence>
          </ThemeToggle>
        </NavLinks>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;
